import styled from "@emotion/styled";

import type { MarginProps } from "styled-system";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import {
  type ButtonHTMLAttributes,
  type ClassAttributes,
  type ForwardedRef,
  forwardRef,
} from "react";
import shouldForwardProp from "@emotion/is-prop-valid";
import { Skeleton } from "src/components/Skeleton/Skeleton.tsx";
import { TooltipV3 } from "src/components/TooltipV3/TooltipV3.tsx";
import type { TooltipProps } from "@radix-ui/react-tooltip";

type Skin = "info" | "danger" | "success";

const getButtonColorStyles = ({
  skin,
  disabled,
  theme,
}: {
  theme: ExactTheme;
  skin: Skin;
  disabled?: boolean;
}) => {
  const { colorsV2 } = theme;

  if (disabled) {
    return {
      background: "transparent",
      color: colorsV2.grey[600],
    };
  }

  const colorName = (
    {
      info: "blue",
      success: "green",
      danger: "red",
    } as const
  )[skin];

  return {
    transition: "150ms",

    background: "transparent",
    color: colorsV2[colorName][500],
    ":hover": {
      background: colorsV2[colorName][50],
    },
    ":active": {
      color: colorsV2[colorName][800],
      background: colorsV2[colorName][100],
    },
    ":focus": {
      outline: "none",
    },
    ":focus-visible": {
      color: "white",
      background: colorsV2[colorName][700],
      outline: `2px solid ${colorsV2[colorName][100]}`,
      // todo: need to consult with the designer about focus styles
    },
  };
};

type Theme = Omit<ExactTheme, "buttons">;

export interface IconButton2Props
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    ClassAttributes<HTMLButtonElement>,
    MarginProps<Theme> {
  disabled?: boolean;
  isLoading?: boolean;
  helperText?: string;
  tooltipProps?: TooltipProps;
  skin: Skin;
}

export const StyledIconButton2 = styled("button", {
  shouldForwardProp: (name) => shouldForwardProp(name) && name !== "color",
})<IconButton2Props>`
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  flex-shrink: 0;
  width: 25px;
  height: 25px;
  font-size: ${({ theme }) => theme.fontSizesV2.body};
  line-height: 1.25;
  background: transparent;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadiusV2.primary};
  :focus {
    outline: none;
  }

  ${getButtonColorStyles}
`;

export const IconButton2 = forwardRef(
  (props: IconButton2Props, ref: ForwardedRef<HTMLButtonElement>) => {
    const {
      isLoading = false,
      skin = "info",
      type = "button",
      helperText,
      children,
      tooltipProps,
      ...rest
    } = props;
    return (
      <Skeleton isLoading={isLoading}>
        <TooltipV3
          contentProps={{
            sideOffset: 5,
            alignOffset: 5,
          }}
          disableHoverableContent={true}
          enabled={!!helperText}
          content={helperText}
          {...tooltipProps}
        >
          <StyledIconButton2 ref={ref} skin={skin} type={type} {...rest}>
            {children}
          </StyledIconButton2>
        </TooltipV3>
      </Skeleton>
    );
  },
);
